import { athleteCounterContext } from "../custom-disciplines/athlete-counter.js";
import {
  isTimerAction,
  timerCommandFromPorts,
  timerScope,
} from "../behavior-timers/protocol.js";
import {
  FUNCTION_FLOW_IN_PORT_ID,
  FUNCTION_FLOW_OUT_PORT_ID,
} from "../blueprint/function-graph.js";
import {
  SET_VARIABLE_FLOW_OUT_PORT_ID,
  SET_VARIABLE_VALUE_PORT_ID,
} from "../blueprint/variable-graph.js";
import {
  coerceCssAnimationDirection,
  coerceCssAnimationFillMode,
  toFiniteNumber,
} from "../blueprint/runtime/helpers.js";
import type { BehaviorNode } from "../blueprint/types.js";
import { createValueCompiler } from "./compileValue.js";
import { resolveBehaviorFlowStateKey } from "./flowState.js";
import { coerceToBoolean } from "./helpers.js";
import { mergeCompiledDependencies, uniqueDependencies } from "./method.js";
import { writeRuntimeVariable } from "./runtimeVariables.js";
import type {
  BarLoaderRuntimeTarget,
  CompileFlowOptions,
  CompiledFlow,
  CompiledFlowInstruction,
  CompiledValue,
  FlowRunResult,
  IndexedGraph,
  RuntimeContext,
  RuntimeAnimationPlayback,
  RuntimeFlowContinuation,
  RuntimeFunctionReturn,
  SceneCatalogDependencyIndex,
} from "./types.js";

type CompiledControlFlowInstruction =
  | {
      type: "ifElse";
      condition: CompiledValue;
    }
  | {
      type: "repeat";
      count: CompiledValue;
    }
  | {
      type: "wait";
      delayMs: CompiledValue;
    }
  | {
      type: "mergeFlows";
    }
  | {
      type: "functionEnd";
    };

type RuntimeFlowInstruction =
  | CompiledFlowInstruction
  | CompiledControlFlowInstruction;

type FlowQueueEntry = {
  nodeId: string;
  ctx: RuntimeContext;
  inputPortId?: string;
};

type CompiledFlowNode = {
  id: string;
  instruction: RuntimeFlowInstruction | null;
  nextPortIds: string[];
};

type AnimationRunResult = {
  played: boolean;
  waitMs: number;
  infinite: boolean;
};

type FunctionCallRunResult = {
  completedReturns: RuntimeFunctionReturn[];
  suspended: boolean;
};

function parseBarLoaderRuntimeTarget(
  value: unknown,
): BarLoaderRuntimeTarget | undefined {
  if (!value || typeof value !== "object") {
    return undefined;
  }

  const target = value as {
    component?: string;
    weight?: unknown;
    part?: unknown;
  };

  if (target.component !== "barLoader") {
    return undefined;
  }

  const weight =
    target.weight === "all"
      ? "all"
      : typeof target.weight === "number" && !Number.isNaN(target.weight)
        ? target.weight
        : undefined;

  const part =
    target.part === "disk" || target.part === "label" ? target.part : undefined;

  if (weight == null || !part) {
    return undefined;
  }

  return { weight, part };
}

const DEFAULT_FLOW_OUT_PORT_ID = "out";

/**
 * Returns all outgoing flow connections from a node port (fan-out support).
 */
export function getFlowConnections(
  indexed: IndexedGraph,
  nodeId: string,
  portId: string,
) {
  return (
    indexed.outputConnectionsByNodeAndPort.get(`${nodeId}:${portId}`) ?? []
  );
}

function getFlowOutPortId(node: BehaviorNode): string {
  if (node.type === "action" && node.kind === "call") {
    return FUNCTION_FLOW_OUT_PORT_ID;
  }

  if (node.type === "action" && node.kind === "setVariable") {
    return SET_VARIABLE_FLOW_OUT_PORT_ID;
  }

  return DEFAULT_FLOW_OUT_PORT_ID;
}

const LIST_DATA_ACTION_KEYS = new Set([
  "setListSource",
  "setFilter",
  "setSort",
]);
const CUSTOM_DISCIPLINE_EXTRA_INPUT_PORTS = {
  timerStart: ["reference", "duration", "unit", "mode"],
  timerSet: ["reference", "duration", "unit", "mode"],
  timerStop: ["reference"],
  timerResume: ["reference"],
  timerReset: ["reference"],
  timerDelete: ["reference"],
  customListSetField: [
    "list",
    "searchKey",
    "searchValue",
    "fieldKey",
    "nextValue",
  ],
  customListSetRows: [
    "list",
    "mode",
    "targetGroupId",
    "persistFields",
    "deleteMissing",
  ],
  customListSetViewRows: ["list", "includeNewRows"],
  customGroupSetField: ["groupId", "fieldKey", "nextValue"],
  customOpenGroup: ["groupId"],
  customSetCurrentGroup: ["groupId"],
  customSetCurrentAthlete: ["athleteId"],
  customOpenModal: ["modalKey", "contextAthleteId", "contextGroupId"],
  customCloseModal: ["modalKey"],
  customPrintModal: ["modalKey"],
  customRunAction: ["actionKey", "contextAthleteId", "contextColumnKey"],
  customSubmitForm: [],
  customIncrementAthleteCount: [],
  customGroupService: [
    "service",
    "groupId",
    "fieldMap",
    "format",
    "athleteId",
    "options",
  ],
} as const;

/** Placeholder used as `value` on multi-input `set` instructions (e.g. `customListSetField`). */
const EMPTY_COMPILED_VALUE: CompiledValue = {
  evaluate: () => undefined,
  dependencies: [],
};

/**
 * `set` action keys whose payload must also be surfaced as a one-shot
 * {@link RuntimeEffect} (`customDiscipline.action`) instead of only a sticky
 * `RuntimePatch`. Used by clickable layout elements in the generic
 * Display Editor (modal buttons) so the host page (e.g. `customGroup.tsx`)
 * can apply the mutation exactly once per click.
 */
const CUSTOM_DISCIPLINE_ACTION_EFFECT_KEYS = new Set<string>([
  "customListSetField",
  "customListSetRows",
  "customListSetViewRows",
  "customGroupSetField",
  "customOpenGroup",
  "customSetCurrentGroup",
  "customSetCurrentAthlete",
  "customOpenModal",
  "customCloseModal",
  "customPrintModal",
  "customRunAction",
  "customSubmitForm",
  "customGroupService",
  "customIncrementAthleteCount",
]);

const BAR_LOADER_ROOT_SET_KEYS = new Set([
  "totalWeight",
  "barWeight",
  "setBinding",
  "location",
  "transform",
]);

function isBarLoaderSubTargetSetKey(key: string): boolean {
  return !LIST_DATA_ACTION_KEYS.has(key) && !BAR_LOADER_ROOT_SET_KEYS.has(key);
}

function compileSetFlowInstruction(
  indexed: IndexedGraph,
  ownerNodeId: string,
  node: BehaviorNode,
  valueCompiler: ReturnType<typeof createValueCompiler>,
  flowOptions?: CompileFlowOptions,
): CompiledFlowInstruction | null {
  if (node.type !== "action" || node.kind !== "set") {
    return null;
  }

  if (LIST_DATA_ACTION_KEYS.has(node.key)) {
    return null;
  }

  const customDisciplineInputPorts =
    CUSTOM_DISCIPLINE_EXTRA_INPUT_PORTS[
      node.key as keyof typeof CUSTOM_DISCIPLINE_EXTRA_INPUT_PORTS
    ];
  if (customDisciplineInputPorts) {
    const extraInputs = Object.fromEntries(
      customDisciplineInputPorts.map((portId) => [
        portId,
        valueCompiler.compileInputValue(node.id, portId),
      ]),
    );
    return {
      type: "set",
      // Use the blueprint set-node id so chained custom-discipline mutations
      // (e.g. two `customListSetField` in one onClick flow) keep distinct patch keys.
      nodeId: node.id,
      key: node.key,
      value:
        extraInputs.nextValue ??
        extraInputs.list ??
        extraInputs.modalKey ??
        EMPTY_COMPILED_VALUE,
      extraInputs,
    };
  }

  const targetPortConnected = indexed.inputConnectionByNodeAndPort.has(
    `${node.id}:target`,
  );
  const barLoaderTargetScope =
    flowOptions?.ownerIsBarLoader === true &&
    targetPortConnected &&
    isBarLoaderSubTargetSetKey(node.key);

  return {
    type: "set",
    nodeId: ownerNodeId,
    key: node.key,
    value: valueCompiler.compileInputValue(node.id, "value"),
    ...(targetPortConnected
      ? { target: valueCompiler.compileInputValue(node.id, "target") }
      : {}),
    ...(barLoaderTargetScope ? { barLoaderTargetScope: true } : {}),
  };
}

function compileCallFunctionFlowInstruction(
  ownerNodeId: string,
  node: BehaviorNode,
  valueCompiler: ReturnType<typeof createValueCompiler>,
  catalogDeps?: SceneCatalogDependencyIndex,
): CompiledFlowInstruction | null {
  if (node.type !== "action" || node.kind !== "call") {
    return null;
  }

  const functionId =
    "functionId" in node && typeof node.functionId === "string"
      ? node.functionId
      : undefined;

  if (!functionId) {
    return null;
  }

  const inputPorts = node.ports.filter(
    (port) =>
      port.direction === "input" &&
      port.valueType !== "flow" &&
      port.id !== FUNCTION_FLOW_IN_PORT_ID &&
      port.id !== FUNCTION_FLOW_OUT_PORT_ID,
  );

  return {
    type: "callFunction",
    nodeId: ownerNodeId,
    callNodeId: node.id,
    functionId,
    functionDependencies:
      catalogDeps?.functionDependenciesById[functionId] ?? [],
    inputs: inputPorts.map((port) => ({
      portId: port.id,
      label: port.label,
      value: valueCompiler.compileInputValue(node.id, port.id),
    })),
  };
}

function compileSetVariableFlowInstruction(
  node: BehaviorNode,
  valueCompiler: ReturnType<typeof createValueCompiler>,
): CompiledFlowInstruction | null {
  if (node.type !== "action" || node.kind !== "setVariable") {
    return null;
  }

  const variableId =
    "variableId" in node && typeof node.variableId === "string"
      ? node.variableId
      : undefined;

  if (!variableId) {
    return null;
  }

  return {
    type: "setVariable",
    variableId,
    value: valueCompiler.compileInputValue(node.id, SET_VARIABLE_VALUE_PORT_ID),
  };
}

function compileFlowInstruction(
  indexed: IndexedGraph,
  ownerNodeId: string,
  node: BehaviorNode,
  valueCompiler: ReturnType<typeof createValueCompiler>,
  catalogDeps?: SceneCatalogDependencyIndex,
  flowOptions?: CompileFlowOptions,
): CompiledFlowInstruction | null {
  if (node.type === "action" && node.kind === "set") {
    return compileSetFlowInstruction(
      indexed,
      ownerNodeId,
      node,
      valueCompiler,
      flowOptions,
    );
  }

  if (node.type === "action" && node.kind === "call") {
    return compileCallFunctionFlowInstruction(
      ownerNodeId,
      node,
      valueCompiler,
      catalogDeps,
    );
  }

  if (node.type === "action" && node.kind === "setVariable") {
    return compileSetVariableFlowInstruction(node, valueCompiler);
  }

  if (node.type === "action" && node.kind === "animPlay") {
    return {
      type: "animPlay",
      nodeId: ownerNodeId,
      animationName: valueCompiler.compileInputValue(node.id, "name"),
      playInfinite: node.playInfinite,
      delayMs: valueCompiler.compileInputValue(node.id, "delayMs"),
      durationMs: valueCompiler.compileInputValue(node.id, "durationMs"),
      iterationCount: valueCompiler.compileInputValue(
        node.id,
        "iterationCount",
      ),
      direction: valueCompiler.compileInputValue(node.id, "direction"),
      fillMode: valueCompiler.compileInputValue(node.id, "fillMode"),
    };
  }

  return null;
}

function compileControlFlowInstruction(
  node: BehaviorNode,
  valueCompiler: ReturnType<typeof createValueCompiler>,
): CompiledControlFlowInstruction | null {
  if (node.type !== "control") {
    return null;
  }

  if (node.controlType === "ifElse") {
    return {
      type: "ifElse",
      condition: valueCompiler.compileInputValue(node.id, "condition"),
    };
  }

  if (node.controlType === "repeat") {
    return {
      type: "repeat",
      count: valueCompiler.compileInputValue(node.id, "count"),
    };
  }

  if (node.controlType === "wait") {
    return {
      type: "wait",
      delayMs: valueCompiler.compileInputValue(node.id, "delayMs"),
    };
  }

  if (node.controlType === "mergeFlows") {
    return { type: "mergeFlows" };
  }

  if (node.controlType === "functionEnd") {
    return { type: "functionEnd" };
  }

  return null;
}

function compileRuntimeFlowInstruction(
  indexed: IndexedGraph,
  ownerNodeId: string,
  valueCompiler: ReturnType<typeof createValueCompiler>,
  node: BehaviorNode,
  catalogDeps?: SceneCatalogDependencyIndex,
  flowOptions?: CompileFlowOptions,
): RuntimeFlowInstruction | null {
  return (
    compileFlowInstruction(
      indexed,
      ownerNodeId,
      node,
      valueCompiler,
      catalogDeps,
      flowOptions,
    ) ?? compileControlFlowInstruction(node, valueCompiler)
  );
}

function getNextPortIdsForNode(
  node: BehaviorNode,
  instruction: RuntimeFlowInstruction | null,
): string[] {
  if (instruction?.type === "ifElse") {
    return ["true", "false"];
  }

  if (instruction?.type === "mergeFlows") {
    return [DEFAULT_FLOW_OUT_PORT_ID];
  }

  if (instruction?.type === "repeat") {
    return ["body", "done"];
  }

  if (instruction?.type === "wait") {
    return [DEFAULT_FLOW_OUT_PORT_ID];
  }

  if (instruction?.type === "functionEnd") {
    return [];
  }

  return [getFlowOutPortId(node)];
}

function compileReachableFlowNodes(
  indexed: IndexedGraph,
  ownerNodeId: string,
  startNodeId: string,
  startPortId: string,
  valueCompiler: ReturnType<typeof createValueCompiler>,
  catalogDeps?: SceneCatalogDependencyIndex,
  flowOptions?: CompileFlowOptions,
): {
  nodesById: Map<string, CompiledFlowNode>;
  startNodeIds: string[];
} {
  const nodesById = new Map<string, CompiledFlowNode>();
  const startNodeIds = getFlowConnections(
    indexed,
    startNodeId,
    startPortId,
  ).map((connection) => connection.toNodeId);
  const queue = [...startNodeIds];

  while (queue.length > 0) {
    const nodeId = queue.shift()!;
    if (nodesById.has(nodeId)) {
      continue;
    }

    const node = indexed.nodesById.get(nodeId);
    if (!node) {
      continue;
    }

    const instruction = compileRuntimeFlowInstruction(
      indexed,
      ownerNodeId,
      valueCompiler,
      node,
      catalogDeps,
      flowOptions,
    );
    const nextPortIds = getNextPortIdsForNode(node, instruction);

    nodesById.set(nodeId, {
      id: nodeId,
      instruction,
      nextPortIds,
    });

    for (const portId of nextPortIds) {
      for (const connection of getFlowConnections(indexed, nodeId, portId)) {
        queue.push(connection.toNodeId);
      }
    }
  }

  return { nodesById, startNodeIds };
}

function collectInstructionDependencies(
  instructions: RuntimeFlowInstruction[],
): string[] {
  const directDependencies: string[] = [];
  const values = instructions.flatMap((instruction) => {
    if (instruction.type === "set") {
      return [
        instruction.value,
        ...(instruction.target ? [instruction.target] : []),
        ...Object.values(instruction.extraInputs ?? {}),
      ];
    }

    if (instruction.type === "setVariable") {
      return [instruction.value];
    }

    if (instruction.type === "animPlay") {
      return [
        instruction.animationName,
        instruction.restart,
        instruction.delayMs,
        instruction.durationMs,
        instruction.iterationCount,
        instruction.direction,
        instruction.fillMode,
      ].filter((value): value is CompiledValue => value != null);
    }

    if (instruction.type === "ifElse") {
      return [instruction.condition];
    }

    if (instruction.type === "repeat") {
      return [instruction.count];
    }

    if (instruction.type === "wait") {
      return [instruction.delayMs];
    }

    if (instruction.type === "mergeFlows") {
      return [];
    }

    if (instruction.type === "functionEnd") {
      return [];
    }

    directDependencies.push(...instruction.functionDependencies);
    return instruction.inputs.map((input) => input.value);
  });

  return uniqueDependencies([
    ...mergeCompiledDependencies(...values),
    ...directDependencies,
  ]);
}

function flowRequiresRuntimeDomTarget(
  instructions: RuntimeFlowInstruction[],
): boolean {
  return instructions.some((instruction) => instruction.type === "animPlay");
}

function resolveFlowInstanceKey(ctx: RuntimeContext, flowKey: string): string {
  if (ctx.__nodeInstanceKey) {
    return `${ctx.__nodeInstanceKey}:${flowKey}`;
  }
  return `${ctx.__nodeInstanceKeyPrefix ?? ""}${flowKey}`;
}

function resolveFlowStateKey(ctx: RuntimeContext, nodeId: string): string {
  return resolveBehaviorFlowStateKey(nodeId, ctx);
}

function loopStackSignature(ctx: RuntimeContext): string {
  const loopStack = ctx.__flowLoopStack ?? [];
  if (loopStack.length === 0) {
    return "root";
  }
  return loopStack.map((loop) => `${loop.nodeId}:${loop.index}`).join("/");
}

function resolveNowMs(ctx: RuntimeContext): number {
  return typeof ctx.__nowMs === "number" && Number.isFinite(ctx.__nowMs)
    ? ctx.__nowMs
    : Date.now();
}

function resolveDelayMs(raw: unknown): number {
  return Math.max(0, Math.floor(toFiniteNumber(raw, 0)));
}

function snapshotContinuationContext(ctx: RuntimeContext): RuntimeContext {
  const {
    __renderCacheByNodeKey: _renderCacheByNodeKey,
    __runtimeContinuationStateByNodeKey: _runtimeContinuationStateByNodeKey,
    __runtimeContinuations: _runtimeContinuations,
    __runtimeEffects: _runtimeEffects,
    __runtimePatchStateByNodeKey: _runtimePatchStateByNodeKey,
    __runtimeVariableStateById: _runtimeVariableStateById,
    __runtimeVariableVersionById: _runtimeVariableVersionById,
    ...continuationCtx
  } = ctx;

  return continuationCtx;
}

function resumeContinuationContext(
  continuation: RuntimeFlowContinuation,
  ctx: RuntimeContext,
): RuntimeContext {
  return {
    ...continuation.ctx,
    __animations: ctx.__animations,
    __animationsByName: ctx.__animationsByName,
    __customEventTriggers: ctx.__customEventTriggers,
    __customEventTriggersByName: ctx.__customEventTriggersByName,
    __customEventTriggerStateByKey: ctx.__customEventTriggerStateByKey,
    __flowStateByNodeKey: ctx.__flowStateByNodeKey,
    __functions: ctx.__functions,
    __nowMs: ctx.__nowMs,
    __renderCacheByNodeKey: ctx.__renderCacheByNodeKey,
    __resumeContinuations: ctx.__resumeContinuations,
    __runtimeContinuationStateByNodeKey:
      ctx.__runtimeContinuationStateByNodeKey,
    __runtimeEffects: ctx.__runtimeEffects,
    __runtimeContinuations: ctx.__runtimeContinuations,
    __runtimePatchStateByNodeKey: ctx.__runtimePatchStateByNodeKey,
    __runtimeVariableStateById: ctx.__runtimeVariableStateById,
    __runtimeVariableVersionById: ctx.__runtimeVariableVersionById,
    __variables: ctx.__variables,
  };
}

function resolveAnimationWaitMs(
  playback: RuntimeAnimationPlayback,
  instruction: Extract<CompiledFlowInstruction, { type: "animPlay" }>,
  ctx: RuntimeContext,
): number {
  const delayMs = instruction.delayMs
    ? toFiniteNumber(
        instruction.delayMs.evaluate(ctx),
        playback.payload.delayMs ?? 0,
      )
    : (playback.payload.delayMs ?? 0);
  const durationMs = instruction.durationMs
    ? toFiniteNumber(
        instruction.durationMs.evaluate(ctx),
        playback.payload.durationMs ?? 0,
      )
    : (playback.payload.durationMs ?? 0);
  const iterationCount = instruction.iterationCount
    ? toFiniteNumber(
        instruction.iterationCount.evaluate(ctx),
        playback.payload.iterationCount ?? 1,
      )
    : (playback.payload.iterationCount ?? 1);

  if (!Number.isFinite(iterationCount)) {
    return 0;
  }

  return (
    Math.max(0, delayMs) + Math.max(0, durationMs) * Math.max(0, iterationCount)
  );
}

function scheduleFlowContinuation(
  result: FlowRunResult,
  flowKey: string,
  suspendNodeId: string,
  targetNodeId: string,
  targetInputPortId: string | undefined,
  ctx: RuntimeContext,
  delayMs: number,
  functionId?: string,
  patchOwnerNodeId?: string,
) {
  const instanceFlowKey = resolveFlowInstanceKey(ctx, flowKey);
  const returnFrame =
    functionId != null
      ? ctx.__functionReturnStack?.[ctx.__functionReturnStack.length - 1]
      : undefined;
  const key = [
    instanceFlowKey,
    suspendNodeId,
    targetNodeId,
    targetInputPortId ?? "in",
    loopStackSignature(ctx),
  ].join("|");

  result.continuations.push({
    key,
    flowKey: instanceFlowKey,
    wakeAt: resolveNowMs(ctx) + delayMs,
    nodeId: targetNodeId,
    inputPortId: targetInputPortId,
    ctx: snapshotContinuationContext(ctx),
    ...(functionId && patchOwnerNodeId
      ? {
          resumeTarget: {
            type: "function" as const,
            functionId,
            patchOwnerNodeId,
            ownerStateKey: resolveFlowStateKey(ctx, patchOwnerNodeId),
            parentFlowKey: returnFrame?.parentFlowKey,
            callNodeId: returnFrame?.callNodeId,
          },
        }
      : {}),
  });
}

function scheduleNextFlowContinuations(
  indexed: IndexedGraph,
  result: FlowRunResult,
  flowKey: string,
  nodeId: string,
  portId: string,
  ctx: RuntimeContext,
  delayMs: number,
  functionId?: string,
  patchOwnerNodeId?: string,
): boolean {
  const connections = getFlowConnections(indexed, nodeId, portId);
  for (const connection of connections) {
    scheduleFlowContinuation(
      result,
      flowKey,
      nodeId,
      connection.toNodeId,
      connection.toPortId,
      ctx,
      delayMs,
      functionId,
      patchOwnerNodeId,
    );
  }
  return connections.length > 0;
}

function scheduleInnermostLoopContinuation(
  result: FlowRunResult,
  flowKey: string,
  suspendNodeId: string,
  ctx: RuntimeContext,
  delayMs: number,
  functionId?: string,
  patchOwnerNodeId?: string,
): boolean {
  const loopStack = ctx.__flowLoopStack ?? [];
  const currentLoop = loopStack[loopStack.length - 1];

  if (!currentLoop) {
    return false;
  }

  scheduleFlowContinuation(
    result,
    flowKey,
    suspendNodeId,
    currentLoop.nodeId,
    undefined,
    ctx,
    delayMs,
    functionId,
    patchOwnerNodeId,
  );
  return true;
}

function runAnimPlayInstruction(
  instruction: Extract<CompiledFlowInstruction, { type: "animPlay" }>,
  ctx: RuntimeContext,
  result: FlowRunResult,
): AnimationRunResult {
  const animationName = String(
    instruction.animationName.evaluate(ctx) ?? "",
  ).trim();

  if (!animationName) {
    return { played: false, waitMs: 0, infinite: false };
  }

  const resolver =
    ctx.__animationsByName?.[animationName.toLocaleLowerCase()] ??
    ctx.__animationsByName?.[animationName];

  const playback = resolver?.(ctx);

  if (!playback) {
    return { played: false, waitMs: 0, infinite: false };
  }

  const targetNodeKey = resolveBehaviorFlowStateKey(instruction.nodeId, ctx);
  const delayMs = instruction.delayMs
    ? toFiniteNumber(instruction.delayMs.evaluate(ctx))
    : undefined;
  const durationMs = instruction.durationMs
    ? toFiniteNumber(instruction.durationMs.evaluate(ctx))
    : undefined;
  const iterationCount = instruction.iterationCount
    ? toFiniteNumber(instruction.iterationCount.evaluate(ctx))
    : undefined;
  const infinite = instruction.playInfinite === true;

  result.effects.push({
    type: "animation.play",
    targetNodeId: instruction.nodeId,
    targetNodeKey,
    payload: playback.payload,
    infinite,
    restart: instruction.restart
      ? Boolean(instruction.restart.evaluate(ctx))
      : true,
    delayMs,
    durationMs,
    iterationCount,
    direction: instruction.direction
      ? coerceCssAnimationDirection(instruction.direction.evaluate(ctx))
      : undefined,
    fillMode: instruction.fillMode
      ? coerceCssAnimationFillMode(instruction.fillMode.evaluate(ctx))
      : undefined,
  });

  return {
    played: true,
    infinite,
    waitMs: resolveAnimationWaitMs(playback, instruction, ctx),
  };
}

function appendFlowRunResult(target: FlowRunResult, source: FlowRunResult) {
  target.patches.push(...source.patches);
  target.effects.push(...source.effects);
  target.continuations.push(...source.continuations);
}

function buildFunctionInputFrame(
  instruction: Extract<CompiledFlowInstruction, { type: "callFunction" }>,
  ctx: RuntimeContext,
) {
  const byPortId: Record<string, unknown> = {};
  const byLabel: Record<string, unknown> = {};

  for (const input of instruction.inputs) {
    const value = input.value.evaluate(ctx);
    byPortId[input.portId] = value;

    if (input.label) {
      byLabel[input.label] = value;
    }
  }

  return { byPortId, byLabel };
}

function runCallFunctionInstruction(
  instruction: Extract<CompiledFlowInstruction, { type: "callFunction" }>,
  ctx: RuntimeContext,
  result: FlowRunResult,
  flowKey: string,
): FunctionCallRunResult {
  const functionRuntime = ctx.__functions?.[instruction.functionId];
  const args = buildFunctionInputFrame(instruction, ctx);

  if (!functionRuntime) {
    result.effects.push({
      type: "callFunction",
      functionId: instruction.functionId,
      output: undefined,
    });
    return {
      completedReturns: [{ portId: FUNCTION_FLOW_OUT_PORT_ID, ctx }],
      suspended: false,
    };
  }

  if (!functionRuntime.runFlow) {
    return {
      completedReturns: [{ portId: FUNCTION_FLOW_OUT_PORT_ID, ctx }],
      suspended: false,
    };
  }

  const childCtx: RuntimeContext = {
    ...ctx,
    __functionReturnStack: [
      ...(ctx.__functionReturnStack ?? []),
      {
        parentFlowKey: resolveFlowInstanceKey(ctx, flowKey),
        callNodeId: instruction.callNodeId,
      },
    ],
  };
  const flowResult = functionRuntime.runFlow(
    childCtx,
    args,
    instruction.nodeId,
  );
  appendFlowRunResult(result, flowResult);

  return {
    completedReturns: flowResult.functionReturns ?? [],
    suspended: (flowResult.continuations.length ?? 0) > 0,
  };
}

function runInstruction(
  instruction: Exclude<CompiledFlowInstruction, { type: "callFunction" }>,
  ctx: RuntimeContext,
  result: FlowRunResult,
): AnimationRunResult | undefined {
  switch (instruction.type) {
    case "set": {
      const target = instruction.target
        ? parseBarLoaderRuntimeTarget(instruction.target.evaluate(ctx))
        : undefined;

      if (instruction.barLoaderTargetScope && target) {
        result.patches.push({
          nodeId: instruction.nodeId,
          key: instruction.key,
          value: instruction.value.evaluate(ctx),
          barLoaderTarget: target,
          barLoaderTargetScope: true,
        });
        return undefined;
      }

      if (instruction.barLoaderTargetScope && !target) {
        return undefined;
      }

      let extraInputs = instruction.extraInputs
        ? Object.fromEntries(
            Object.entries(instruction.extraInputs).map(
              ([portId, compiled]) => [portId, compiled.evaluate(ctx)],
            ),
          )
        : undefined;

      if (
        instruction.key === "customSetCurrentAthlete" &&
        extraInputs &&
        (extraInputs.athleteId === null || extraInputs.athleteId === undefined)
      ) {
        const contextAthlete = ctx.athlete;
        const contextAthleteId =
          contextAthlete && typeof contextAthlete === "object"
            ? (contextAthlete as { id?: unknown }).id
            : undefined;
        extraInputs = {
          ...extraInputs,
          athleteId:
            String(contextAthleteId ?? "").trim() ||
            String(ctx.selectedRowId ?? "").trim(),
        };
      }

      if (instruction.key === "customIncrementAthleteCount") {
        extraInputs = athleteCounterContext(ctx);
      }

      if (isTimerAction(instruction.key) && extraInputs) {
        // Commands are one-shot effects, never persistent render/style patches.
        const command = timerCommandFromPorts(
          instruction.key,
          extraInputs,
          timerScope(ctx),
        );
        result.effects.push({
          type: "customDiscipline.action",
          nodeId: instruction.nodeId,
          action: instruction.key,
          payload: command,
        });
        return undefined;
      }
      result.patches.push({
        nodeId: instruction.nodeId,
        key: instruction.key,
        value: extraInputs ?? instruction.value.evaluate(ctx),
      });

      if (
        extraInputs &&
        CUSTOM_DISCIPLINE_ACTION_EFFECT_KEYS.has(instruction.key)
      ) {
        result.effects.push({
          type: "customDiscipline.action",
          nodeId: instruction.nodeId,
          action: instruction.key as
            | "customListSetField"
            | "customListSetRows"
            | "customListSetViewRows"
            | "customGroupSetField"
            | "customOpenGroup"
            | "customSetCurrentGroup"
            | "customSetCurrentAthlete"
            | "customOpenModal"
            | "customCloseModal"
            | "customPrintModal"
            | "customRunAction"
            | "customSubmitForm"
            | "customGroupService"
            | "customIncrementAthleteCount",
          payload: extraInputs,
        });
      }
      return undefined;
    }

    case "setVariable": {
      writeRuntimeVariable(
        ctx,
        instruction.variableId,
        instruction.value.evaluate(ctx),
      );
      return undefined;
    }

    case "animPlay": {
      return runAnimPlayInstruction(instruction, ctx, result);
    }
  }
}

function enqueueNextFlowNodes(
  indexed: IndexedGraph,
  queue: FlowQueueEntry[],
  nodeId: string,
  portId: string,
  ctx: RuntimeContext,
) {
  for (const connection of getFlowConnections(indexed, nodeId, portId)) {
    queue.push({
      nodeId: connection.toNodeId,
      ctx,
      inputPortId: connection.toPortId,
    });
  }
}

function coerceRepeatCount(raw: unknown): number {
  const count = Number(raw);
  if (!Number.isFinite(count)) {
    return 0;
  }
  return Math.max(0, Math.floor(count));
}

function enqueueInnermostLoopContinuation(
  queue: FlowQueueEntry[],
  ctx: RuntimeContext,
): boolean {
  const loopStack = ctx.__flowLoopStack ?? [];
  const currentLoop = loopStack[loopStack.length - 1];

  if (!currentLoop) {
    return false;
  }

  queue.push({ nodeId: currentLoop.nodeId, ctx });
  return true;
}

function pushFunctionReturn(result: FlowRunResult, entry: FlowQueueEntry) {
  const inputPortId = entry.inputPortId ?? FUNCTION_FLOW_IN_PORT_ID;
  const returnPortId =
    inputPortId === FUNCTION_FLOW_IN_PORT_ID
      ? FUNCTION_FLOW_OUT_PORT_ID
      : inputPortId;
  const returnStack = entry.ctx.__functionReturnStack ?? [];
  const functionCallFrameStack = entry.ctx.__functionCallFrameStack ?? [];
  const functionEvalStack = entry.ctx.__functionEvalStack ?? [];

  const nextCtx: RuntimeContext = {
    ...entry.ctx,
    __functionReturnStack: returnStack.slice(0, -1),
    __functionCallFrameStack: functionCallFrameStack.slice(0, -1),
    __functionEvalStack: functionEvalStack.slice(0, -1),
  };

  result.functionReturns = [
    ...(result.functionReturns ?? []),
    {
      portId: returnPortId,
      ctx: nextCtx,
    },
  ];
}

function runCompiledFlowNode(
  compiledNode: CompiledFlowNode,
  indexed: IndexedGraph,
  entry: FlowQueueEntry,
  result: FlowRunResult,
  queue: FlowQueueEntry[],
  flowKey: string,
  functionId?: string,
  patchOwnerNodeId?: string,
) {
  const node = compiledNode;
  const ctx = entry.ctx;
  const instruction = node.instruction;
  const queueLengthBefore = queue.length;

  if (!instruction) {
    for (const portId of node.nextPortIds) {
      enqueueNextFlowNodes(indexed, queue, node.id, portId, ctx);
    }
    if (queue.length === queueLengthBefore) {
      enqueueInnermostLoopContinuation(queue, ctx);
    }
    return;
  }

  if (instruction.type === "ifElse") {
    const branch = coerceToBoolean(instruction.condition.evaluate(ctx))
      ? "true"
      : "false";
    enqueueNextFlowNodes(indexed, queue, node.id, branch, ctx);
    if (queue.length === queueLengthBefore) {
      enqueueInnermostLoopContinuation(queue, ctx);
    }
    return;
  }

  if (instruction.type === "repeat") {
    const count = coerceRepeatCount(instruction.count.evaluate(ctx));
    const loopStack = ctx.__flowLoopStack ?? [];
    const currentLoop = loopStack[loopStack.length - 1];
    const isCurrentLoop = currentLoop?.nodeId === node.id;

    if (!isCurrentLoop) {
      if (count <= 0) {
        enqueueNextFlowNodes(indexed, queue, node.id, "done", ctx);
        if (queue.length === queueLengthBefore) {
          enqueueInnermostLoopContinuation(queue, ctx);
        }
        return;
      }

      enqueueNextFlowNodes(indexed, queue, node.id, "body", {
        ...ctx,
        __flowLoopStack: [...loopStack, { nodeId: node.id, index: 0 }],
      });
      return;
    }

    const nextIndex = (currentLoop?.index ?? 0) + 1;
    if (nextIndex < count) {
      const nextLoopStack = [...loopStack];
      nextLoopStack[nextLoopStack.length - 1] = {
        nodeId: node.id,
        index: nextIndex,
      };
      enqueueNextFlowNodes(indexed, queue, node.id, "body", {
        ...ctx,
        __flowLoopStack: nextLoopStack,
      });
      return;
    }

    const nextCtx: RuntimeContext = {
      ...ctx,
      __flowLoopStack: loopStack.slice(0, -1),
    };
    enqueueNextFlowNodes(indexed, queue, node.id, "done", nextCtx);
    if (queue.length === queueLengthBefore) {
      enqueueInnermostLoopContinuation(queue, nextCtx);
    }
    return;
  }

  if (instruction.type === "wait") {
    if (ctx.__fastForwardAsyncFlows === true) {
      enqueueNextFlowNodes(
        indexed,
        queue,
        node.id,
        DEFAULT_FLOW_OUT_PORT_ID,
        ctx,
      );
      if (queue.length === queueLengthBefore) {
        enqueueInnermostLoopContinuation(queue, ctx);
      }
      return;
    }

    const delayMs = resolveDelayMs(instruction.delayMs.evaluate(ctx));
    const hasOutgoingContinuations = scheduleNextFlowContinuations(
      indexed,
      result,
      flowKey,
      node.id,
      DEFAULT_FLOW_OUT_PORT_ID,
      ctx,
      delayMs,
      functionId,
      patchOwnerNodeId,
    );
    if (!hasOutgoingContinuations) {
      scheduleInnermostLoopContinuation(
        result,
        flowKey,
        node.id,
        ctx,
        delayMs,
        functionId,
        patchOwnerNodeId,
      );
    }
    return;
  }

  if (instruction.type === "mergeFlows") {
    enqueueNextFlowNodes(
      indexed,
      queue,
      node.id,
      DEFAULT_FLOW_OUT_PORT_ID,
      ctx,
    );
    if (queue.length === queueLengthBefore) {
      enqueueInnermostLoopContinuation(queue, ctx);
    }
    return;
  }

  if (instruction.type === "functionEnd") {
    pushFunctionReturn(result, entry);
    return;
  }

  if (instruction.type === "callFunction") {
    const functionResult = runCallFunctionInstruction(
      instruction,
      ctx,
      result,
      flowKey,
    );

    for (const completedReturn of functionResult.completedReturns) {
      enqueueNextFlowNodes(
        indexed,
        queue,
        node.id,
        completedReturn.portId,
        completedReturn.ctx,
      );
    }

    if (
      !functionResult.suspended &&
      functionResult.completedReturns.length === 0 &&
      queue.length === queueLengthBefore
    ) {
      enqueueInnermostLoopContinuation(queue, ctx);
    }
    return;
  }

  if (instruction.type === "animPlay" && ctx.__fastForwardAsyncFlows === true) {
    enqueueNextFlowNodes(
      indexed,
      queue,
      node.id,
      DEFAULT_FLOW_OUT_PORT_ID,
      ctx,
    );
    if (queue.length === queueLengthBefore) {
      enqueueInnermostLoopContinuation(queue, ctx);
    }
    return;
  }

  const instructionResult = runInstruction(instruction, ctx, result);

  if (instruction.type === "animPlay") {
    if (!instructionResult?.played || instructionResult.infinite) {
      return;
    }

    const hasOutgoingContinuations = scheduleNextFlowContinuations(
      indexed,
      result,
      flowKey,
      node.id,
      DEFAULT_FLOW_OUT_PORT_ID,
      ctx,
      instructionResult.waitMs,
      functionId,
      patchOwnerNodeId,
    );
    if (!hasOutgoingContinuations) {
      scheduleInnermostLoopContinuation(
        result,
        flowKey,
        node.id,
        ctx,
        instructionResult.waitMs,
        functionId,
        patchOwnerNodeId,
      );
    }
    return;
  }

  for (const portId of node.nextPortIds) {
    enqueueNextFlowNodes(indexed, queue, node.id, portId, ctx);
  }
  if (queue.length === queueLengthBefore) {
    enqueueInnermostLoopContinuation(queue, ctx);
  }
}

const MAX_FLOW_STEPS = 1000;

/**
 * Compiles a flow graph starting from an event or action output port.
 * Follows every outgoing flow edge (fan-out from the same port is supported).
 *
 * @param ownerNodeId - Layout node that owns the behavior graph (patch target).
 */
export function compileFlowFrom(
  indexed: IndexedGraph,
  ownerNodeId: string,
  startNodeId: string,
  startPortId: string,
  catalogDeps?: SceneCatalogDependencyIndex,
  flowOptions?: CompileFlowOptions,
): CompiledFlow {
  const baseFlowKey = [ownerNodeId, startNodeId, startPortId].join(":");
  const valueCompiler = createValueCompiler(indexed, catalogDeps);
  const compiledFlow = compileReachableFlowNodes(
    indexed,
    ownerNodeId,
    startNodeId,
    startPortId,
    valueCompiler,
    catalogDeps,
    flowOptions,
  );
  const instructions = Array.from(compiledFlow.nodesById.values())
    .map((node) => node.instruction)
    .filter(
      (instruction): instruction is RuntimeFlowInstruction =>
        instruction !== null,
    );

  const dependencies = collectInstructionDependencies(instructions);
  const requiresRuntimeDomTarget = flowRequiresRuntimeDomTarget(instructions);
  const functionId = flowOptions?.functionId;

  return {
    dependencies,
    requiresRuntimeDomTarget,
    run(ctx: RuntimeContext, options?: { resumeOnly?: boolean }) {
      const result: FlowRunResult = {
        patches: [],
        effects: [],
        continuations: [],
      };

      const flowKey = resolveFlowInstanceKey(ctx, baseFlowKey);
      const resumed = (ctx.__resumeContinuations ?? [])
        .filter((continuation) => continuation.flowKey === flowKey)
        .map((continuation) => ({
          nodeId: continuation.nodeId,
          inputPortId: continuation.inputPortId,
          ctx: resumeContinuationContext(continuation, ctx),
        }));
      const resumedFunctionContinuations = (
        ctx.__resumeContinuations ?? []
      ).filter(
        (continuation) =>
          continuation.resumeTarget?.type === "function" &&
          continuation.resumeTarget.parentFlowKey === flowKey,
      );
      const queue: FlowQueueEntry[] =
        options?.resumeOnly === true
          ? resumed
          : compiledFlow.startNodeIds.map((nodeId) => ({ nodeId, ctx }));
      let steps = 0;

      if (options?.resumeOnly === true) {
        for (const continuation of resumedFunctionContinuations) {
          const resumeTarget = continuation.resumeTarget;
          if (resumeTarget?.type !== "function") {
            continue;
          }

          const functionRuntime = ctx.__functions?.[resumeTarget.functionId];
          if (!functionRuntime?.resumeFlow) {
            continue;
          }

          const flowResult = functionRuntime.resumeFlow(ctx, continuation);
          appendFlowRunResult(result, flowResult);

          for (const completedReturn of flowResult.functionReturns ?? []) {
            if (!resumeTarget.callNodeId) {
              continue;
            }
            enqueueNextFlowNodes(
              indexed,
              queue,
              resumeTarget.callNodeId,
              completedReturn.portId,
              completedReturn.ctx,
            );
          }
        }
      }

      while (queue.length > 0 && steps < MAX_FLOW_STEPS) {
        steps += 1;
        const entry = queue.shift()!;
        const node = compiledFlow.nodesById.get(entry.nodeId);

        if (!node) {
          continue;
        }

        runCompiledFlowNode(
          node,
          indexed,
          entry,
          result,
          queue,
          baseFlowKey,
          functionId,
          ownerNodeId,
        );
      }

      return result;
    },
  };
}
